import * as Yup from "yup";

export function getValidation(planType, stepName) {
  // console.log(
  //   "In getValidation: planType =",
  //   planType,
  //   ", stepName =",
  //   stepName,
  // );
  const validationSchemas = {
    plans: Yup.object({
      newPlan: Yup.string().required("Please select a plan type"),
    }),
    plan_type: Yup.object({
      type: Yup.string().required("Plan type is required"),
    }),
    plan_status: Yup.object({
      movedPlan: Yup.string().required(
        "Please indicate if you currently have a plan",
      ),
      midYearPlan: Yup.string().when("movedPlan", {
        is: (value) => value === "Yes",
        then: (schema) =>
          schema.required(
            "Please let us know if Salusion will replace your existing plan",
          ),
        otherwise: (schema) => schema,
      }),
    }),
    classes: Yup.object({
      classes_required: Yup.string().oneOf(
        ["yes", ""],
        "Please add at least one employee class before continuing",
      ),
      classes_complete_required: Yup.string().oneOf(
        ["", undefined],
        "You must complete all employee classes",
      ),
    }),
    employee_groups: Yup.object({
      predefinedClasses_required: Yup.string().oneOf(
        ["yes"],
        "Please select at least one employee group to proceed",
      ),
      employmentGroup_required: Yup.string().oneOf(
        ["yes"],
        "Include at least one of Full-time, Part-time, or Seasonal employees before continuing",
      ),
    }),
    eligible_expenses: Yup.object({
      qualifiedBenefits_required: Yup.string()
        .required(
          "Please select at least one eligible expense before continuing",
        )
        .oneOf(
          ["yes"],
          "Please select at least one eligible expense before continuing",
        ),
      otherBenefit_required: Yup.string(),
      otherBenefit: Yup.string().when("otherBenefit_required", {
        is: "yes",
        then: (schema) =>
          schema
            .trim()
            .required(
              "Please describe the other eligible expense you selected",
            ),
      }),
    }),
    eligible_individuals: Yup.object({
      eligibleIndividuals_required: Yup.string()
        .required(
          "Please select at least one eligible individual option before continuing",
        )
        .oneOf(
          ["yes"],
          "Please select at least one eligible individual option before continuing",
        ),
    }),
    allowance_type: Yup.object({
      allowanceMethod_required: Yup.string()
        .required("Please select an allowance method before continuing")
        .oneOf(["yes"], "Please select an allowance method before continuing"),
    }),
    planDates: Yup.object({
      startDate: Yup.date().required("Start date is required"),
      endDate: Yup.date()
        .required("End date is required")
        .min(Yup.ref("startDate"), "End date must be after start date"),
    }),
    allowance: Yup.object({
      maxBenefit_required: Yup.string().when("allowanceMethod", {
        is: (allowanceMethod) => {
          if (!allowanceMethod || typeof allowanceMethod !== "object")
            return true;
          const method = Object.keys(allowanceMethod).find(
            (key) => allowanceMethod[key] === true,
          );
          return method === "MANUAL" || !method;
        },
        then: (schema) => schema.oneOf(["yes"], "Please select a benefit type"),
        otherwise: (schema) => schema,
      }),
      monthlySelfAmount: Yup.mixed()
        .nullable()
        .when(["allowanceMethod", "maxBenefit"], {
          is: (allowanceMethod, maxBenefit) => {
            const method =
              allowanceMethod && typeof allowanceMethod === "object"
                ? Object.keys(allowanceMethod).find(
                    (key) => allowanceMethod[key] === true,
                  )
                : null;
            const isManual = method === "MANUAL" || !method;
            // Self amount is required for manual entry when:
            // - SELF is selected, OR
            // - FAMILY is selected, OR
            // - Nothing is selected (default case)
            // BUT NOT when AGE or FAMILY_AGE is selected (those use age tables)
            if (!isManual) return false;
            if (maxBenefit?.AGE || maxBenefit?.FAMILY_AGE) return false;
            return (
              maxBenefit?.SELF ||
              maxBenefit?.FAMILY ||
              (!maxBenefit?.SELF &&
                !maxBenefit?.FAMILY &&
                !maxBenefit?.AGE &&
                !maxBenefit?.FAMILY_AGE)
            );
          },
          then: (schema) =>
            schema
              .required("Self amount is required")
              .test(
                "is-valid-amount",
                "Self amount must be greater than or equal to 0",
                (value) => {
                  if (value === null || value === undefined || value === "")
                    return false;
                  const num = Number(value);
                  return !isNaN(num) && num >= 0;
                },
              ),
          otherwise: (schema) => schema.nullable(),
        }),
      monthlyFirstDependentAmount: Yup.mixed()
        .nullable()
        .when(
          ["allowanceMethod", "maxBenefit", "monthlyAdditionalDependentAmount"],
          {
            is: (
              allowanceMethod,
              maxBenefit,
              monthlyAdditionalDependentAmount,
            ) => {
              const method =
                allowanceMethod && typeof allowanceMethod === "object"
                  ? Object.keys(allowanceMethod).find(
                      (key) => allowanceMethod[key] === true,
                    )
                  : null;
              const isManual = method === "MANUAL" || !method;
              // If additional dependent amount exists, first dependent amount is required
              const hasAdditionalDependent =
                monthlyAdditionalDependentAmount !== null &&
                monthlyAdditionalDependentAmount !== undefined &&
                monthlyAdditionalDependentAmount !== "";
              return isManual && maxBenefit?.FAMILY && hasAdditionalDependent;
            },
            then: (schema) =>
              schema
                .required("First dependent amount is required")
                .test(
                  "is-valid-amount",
                  "First dependent amount must be greater than or equal to 0",
                  (value) => {
                    if (value === null || value === undefined || value === "")
                      return false;
                    const num = Number(value);
                    return !isNaN(num) && num >= 0;
                  },
                ),
            otherwise: (schema) =>
              schema
                .nullable()
                .test(
                  "is-valid-amount",
                  "First dependent amount must be greater than or equal to 0",
                  (value) => {
                    // Only validate format if value is provided
                    if (value === null || value === undefined || value === "")
                      return true;
                    const num = Number(value);
                    return !isNaN(num) && num >= 0;
                  },
                ),
          },
        ),
      monthlyAdditionalDependentAmount: Yup.mixed()
        .nullable()
        .when(["allowanceMethod", "maxBenefit"], {
          is: (allowanceMethod, maxBenefit) => {
            const method =
              allowanceMethod && typeof allowanceMethod === "object"
                ? Object.keys(allowanceMethod).find(
                    (key) => allowanceMethod[key] === true,
                  )
                : null;
            const isManual = method === "MANUAL" || !method;
            return isManual && maxBenefit?.FAMILY;
          },
          then: (schema) =>
            schema.test(
              "is-valid-amount",
              "Additional dependent amount must be greater than or equal to 0",
              (value) => {
                // Only validate format if value is provided
                if (value === null || value === undefined || value === "")
                  return true;
                const num = Number(value);
                return !isNaN(num) && num >= 0;
              },
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      age_qualify: Yup.string()
        .nullable()
        .when("maxBenefit", {
          is: (maxBenefit) => maxBenefit?.AGE || maxBenefit?.FAMILY_AGE,
          then: (schema) =>
            schema.oneOf(
              ["Yes"],
              "Amounts must stay the same or increase with age, and no employee amount can be more than 3 times the age 21 amount.",
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      selfAmountExceedsMax: Yup.string()
        .nullable()
        .when("maxBenefit", {
          is: (maxBenefit) => maxBenefit?.SELF || maxBenefit?.FAMILY,
          then: (schema) =>
            schema.oneOf(
              ["Yes"],
              "Monthly self amount exceeds the IRS maximum",
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      familyAmountExceedsMax: Yup.string()
        .nullable()
        .when("maxBenefit", {
          is: (maxBenefit) => maxBenefit?.FAMILY,
          then: (schema) =>
            schema.oneOf(
              ["Yes"],
              "Monthly self + first dependent amount exceeds the IRS family maximum",
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      acaSalary: Yup.mixed()
        .nullable()
        .when("allowanceMethod", {
          is: (allowanceMethod) => {
            if (!allowanceMethod || typeof allowanceMethod !== "object")
              return false;
            const method = Object.keys(allowanceMethod).find(
              (key) => allowanceMethod[key] === true,
            );
            return method === "ACA_MINIMUM";
          },
          then: (schema) =>
            schema
              .test(
                "is-required",
                "Minimum salary for ACA calculation is required",
                (value) => {
                  return value !== null && value !== undefined && value !== "";
                },
              )
              .test(
                "is-greater-than-zero",
                "Minimum salary must be greater than 0",
                (value) => {
                  if (value === null || value === undefined || value === "")
                    return true; // Skip this test if empty (handled by required test)
                  const num = Number(value);
                  return !isNaN(num) && num > 0;
                },
              ),
          otherwise: (schema) => schema.nullable(),
        }),
      metalLevel: Yup.string()
        .nullable()
        .when("allowanceMethod", {
          is: (allowanceMethod) => {
            if (!allowanceMethod || typeof allowanceMethod !== "object")
              return false;
            const method = Object.keys(allowanceMethod).find(
              (key) => allowanceMethod[key] === true,
            );
            return method === "PCT_OF_METAL";
          },
          then: (schema) =>
            schema
              .required("Metal level is required")
              .oneOf(
                ["Bronze", "Silver", "Gold"],
                "Please select a valid metal level",
              ),
          otherwise: (schema) => schema.nullable(),
        }),
      metalPercent: Yup.mixed()
        .nullable()
        .when("allowanceMethod", {
          is: (allowanceMethod) => {
            if (!allowanceMethod || typeof allowanceMethod !== "object")
              return false;
            const method = Object.keys(allowanceMethod).find(
              (key) => allowanceMethod[key] === true,
            );
            return method === "PCT_OF_METAL";
          },
          then: (schema) =>
            schema.when("allowanceMethod", {
              is: (allowanceMethod) => {
                if (!allowanceMethod || typeof allowanceMethod !== "object")
                  return false;
                const method = Object.keys(allowanceMethod).find(
                  (key) => allowanceMethod[key] === true,
                );
                return method === "PCT_OF_METAL";
              },
              then: (schema) =>
                schema
                  .test(
                    "is-required",
                    "Percent of plan to cover is required",
                    (value) => {
                      return (
                        value !== null && value !== undefined && value !== ""
                      );
                    },
                  )
                  .test(
                    "is-greater-than-zero",
                    "Percent of plan to cover must be greater than 0",
                    (value) => {
                      if (value === null || value === undefined || value === "")
                        return true; // Skip this test if empty (handled by required test)
                      const num = Number(value);
                      return !isNaN(num) && num > 0 && num <= 100;
                    },
                  ),
              otherwise: (schema) =>
                schema
                  .test(
                    "is-required",
                    "Percent of plan to cover is required",
                    (value) => {
                      return (
                        value !== null && value !== undefined && value !== ""
                      );
                    },
                  )
                  .test(
                    "is-greater-than-zero",
                    "Percent of plan to cover must be greater than 0",
                    (value) => {
                      if (value === null || value === undefined || value === "")
                        return true; // Skip this test if empty (handled by required test)
                      const num = Number(value);
                      return !isNaN(num) && num > 0 && num <= 100;
                    },
                  ),
            }),
          otherwise: (schema) => schema.nullable(),
        }),
      metalPercentDependent: Yup.mixed()
        .nullable()
        .when("allowanceMethod", {
          is: (allowanceMethod) => {
            if (!allowanceMethod || typeof allowanceMethod !== "object")
              return false;
            const method = Object.keys(allowanceMethod).find(
              (key) => allowanceMethod[key] === true,
            );
            return method === "PCT_OF_METAL";
          },
          then: (schema) =>
            schema.test(
              "is-valid-dependent-percent",
              "Percent for spouse / dependent must be between 0 and 100",
              (value) => {
                // Format only; presence for metalV2 is enforced in the component's handleNext.
                if (value === null || value === undefined || value === "")
                  return true;
                const num = Number(value);
                return !isNaN(num) && num >= 0 && num <= 100;
              },
            ),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
    text_to_confirm: Yup.object({
      allowTextSubmission: Yup.string()
        .required("Please select whether to enable Text-To-Confirm")
        .oneOf(["Yes", "No"], "Please select Yes or No"),
    }),
    reimbursement_method: Yup.object({
      reimbursementMethod: Yup.mixed()
        .required("Please select a reimbursement method")
        .test("has-value", "Please select a reimbursement method", (value) => {
          if (!value || typeof value !== "object") return false;
          return Boolean(value.value);
        }),
    }),
    reimbursement_timing: Yup.object({
      reimbursementTiming: Yup.mixed()
        .required("Please select a reimbursement timing")
        .test("has-value", "Please select a reimbursement timing", (value) => {
          if (!value || typeof value !== "object") return false;
          return Boolean(value.value);
        }),
    }),
    // Add more validation schemas for other steps as needed
  };

  const normalizedStepName =
    typeof stepName === "string"
      ? stepName.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_")
      : undefined;

  return {
    validationSchema: validationSchemas[normalizedStepName] || Yup.object({}),
  };
}

export default getValidation;
